import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from "vuex-persistedstate";
import {localStorageEnabled} from '@/utils/localstorageUtil';
import {
    BusinessResidence,
    CartDataRequest,
    CartError,
    CatItemBeingProcessed,
    CityState,
    CustomerInfo,
    CustomerWithContract,
    FAKE_TAVERN_CAT_ID,
    FAKE_TAVERN_NAME,
    FAKE_TAVERN_SERVICE_ID,
    InstallationOption,
    Product,
    SelectedLocation,
    SelectedOptions,
    SelectedService,
    SelectedServiceProduct,
    Service,
    ServiceAddress,
    ServiceType,
    ServiceTypeId,
    ShoppingCartData,
    Validation
} from './products-and-services/ProductsAndServicesModels';
import {CrossSale, CrossSaleAudienceType, CrossSaleCatalogItem} from './products-and-services/CrossSaleModels';
import {
    cartData,
    getStates,
    loadCrossSales
} from '@/products-and-services/ProductsAndServicesService';

interface NotificationData {
    networkAccessChargeMessageShown: boolean;
}

interface State {
    loadingCart: boolean;
    shoppingCartData: ShoppingCartData;
    storedItem: SelectedServiceProduct | null;
    validation: Validation;
    states: CityState[];
    crossSales: CrossSale[];
    currentRoute: string;
    storedCrossSaleCatalogItem: CrossSaleCatalogItem | null;
    isCustomer: boolean;
    emailForPromotions: string;
    isEmployee: boolean;
    dateForScheduledCall: string;
    timeRangeForScheduledCall: string;
    selectedInstallationOption: InstallationOption;
    siblingProductsRemoveList: any;
    productIdForCrossSaleCatalogItem: string;
    shouldShowFiberComingSoon: boolean;
    catItemBeingProcessed: CatItemBeingProcessed;
    notifications: NotificationData;
}

Vue.use(Vuex);

const plugins = [];
if (localStorageEnabled()) {
    plugins.push(createPersistedState({ paths: ['shoppingCartData', 'emailForPromotions'] }));
}

export default new Vuex.Store({
    state: {
        loadingCart: false,
        shoppingCartData: {} as ShoppingCartData,
        storedItem: null,
        validation: {},
        states: [],
        crossSales: [],
        currentRoute: '',
        storedCrossSaleCatalogItem: null,
        isCustomer: false,
        emailForPromotions: '',
        isEmployee: false,
        dateForScheduledCall: '',
        timeRangeForScheduledCall: '',
        selectedInstallationOption: '' as InstallationOption,
        siblingProductsRemoveList:
                                    {
                                      'CBSEMAL': ['FREECBS','CBS50','CBS75','CBS100'] ,
                                      'FREECBS': ['CBS50','CBS75','CBS100'] ,
                                      'CBS50':   ['FREECBS', 'CBS75','CBS100'] ,
                                      'CBS75':   ['FREECBS','CBS50','CBS100'] ,
                                      'CBS100':  ['FREECBS','CBS50','CBS75'],
                                         '829':  ['829A']
                                    },
        productIdForCrossSaleCatalogItem: '',
        shouldShowFiberComingSoon: false,
        catItemBeingProcessed: {},
        notifications: {
            networkAccessChargeMessageShown: false
        }
    },

    mutations: {
        updateCartServiceLocation(state: State, location: SelectedLocation) {
            const orderDetails = state.shoppingCartData;
            // if new region reset services
            if (orderDetails.regionId !== location.regionId && orderDetails.selectedOptions) {
                orderDetails.selectedOptions.services = [];
            }
            state.shoppingCartData = {...orderDetails, ...(location || {})};
        },
        addToCart(state: State, {serviceId, serviceCatId, productId, productCatId, value}: SelectedServiceProduct) {
            const selectedOptions = state.shoppingCartData.selectedOptions || { services: [] as Service[] };
            const services = [...(selectedOptions.services || [])];
            if (services.some(svc => isEqual(svc.serviceId, serviceId) && isEqual(svc.catId, serviceCatId) && productCatId && productId)) {
                // Add product to already added service
                selectedOptions.services = services.map(service => {
                    if (isEqual(service.serviceId, serviceId) && isEqual(service.catId, serviceCatId) ) {
                        const products = Array.from(new Set([...(service.products || []), {productId, catId: productCatId, value} as Product]));
                        return {...service, products};
                    }
                    return service;
                });
            } else if (serviceId && serviceCatId && productId && productCatId) {
                selectedOptions.services =
                    [...services, {serviceId, catId: serviceCatId, products: [{productId, catId: productCatId, value}]} as Service];
            }
            else  if (serviceId && serviceCatId) {
                selectedOptions.services = [...services, ({serviceId, catId: serviceCatId, value, products: []} as any as Service)];
            } else {
                selectedOptions.services = [...services];
            }
            state.shoppingCartData.selectedOptions = selectedOptions;
        },
        removeFromCart(state: State, item: SelectedServiceProduct) {
            if (item.serviceCatId === FAKE_TAVERN_CAT_ID) {
                state.shoppingCartData.isTavern = false;
            }

            let removeSiblings: any[] = [];
            // only remove siblings when a refetch is occuring
            if (!item.noRefetch) {
                removeSiblings = state.siblingProductsRemoveList[item.productCatId || ''] || [];
            }

            const selectedOptions = state.shoppingCartData.selectedOptions || { services: [] as Service[] } as SelectedOptions;
            const services = [...(selectedOptions.services || [])];
            selectedOptions.services = services.reduce((acc: Service[], svc: Service) => {
                if (isEqual(svc.serviceId, item.serviceId) && isEqual(svc.catId, item.serviceCatId)) {
                    if (!(item.productId && item.productCatId)) {
                        // remove the service
                        return acc;
                    }
                    const products = (svc.products || [])
                        .filter((prod: Product) => !removeSiblings.includes(prod.catId) && (
                                                !isEqual(prod.productId, item.productId)
                                                || !isEqual(prod.catId, item.productCatId)));
                    // return filtered products
                    return [...acc, {...svc, products}];
                }
                return [...acc, svc];
            }, []);
            state.shoppingCartData.selectedOptions = selectedOptions;
        },
        clearShoppingCart(state: State) {
            state.shoppingCartData = {} as ShoppingCartData;
            state.storedItem = null;
            state.validation = {};
            state.states = [];
            state.isCustomer = false;
            state.shoppingCartData.errors = [] as CartError[];
        },
        setLoadingCart(state: State, loadingCart: boolean) {
            state.loadingCart = loadingCart;
        },
        setShoppingCart(state: State, cartData: ShoppingCartData) {
            //don't overwrite queryLogId if there is no order id
            const queryLogId = (cartData && cartData.id) ? '' : state.shoppingCartData.queryLogId
            state.shoppingCartData = {...cartData, queryLogId};
        },
        storeItem(state: State, item: SelectedServiceProduct) {
            state.storedItem = item;
        },
        clearStoredItem(state: State) {
            state.storedItem = null;
        },
        updateValidation(state: State, item: Validation) {
            state.validation = {...state.validation, ...item};
        },
        clearValidation(state: State) {
            state.validation = {};
        },
        setStates(state: State, states: CityState[]) {
            state.states = states;
        },
        setCustomer(state: State, customer: CustomerWithContract) {
            state.shoppingCartData.selectedContractId = customer.selectedContractId;
            state.shoppingCartData.customer = customer;
            state.isCustomer = true;
        },
        setQueryLogId(state: State, queryLogId: string) {
            state.shoppingCartData.queryLogId = queryLogId;
        },
        setCrossSales(state: State, crossSales: CrossSale[]) {
            state.crossSales = crossSales;
        },
        setCurrentRoute: (state: State, currentRoute: string) => {
            state.currentRoute = currentRoute;
        },
        storeCrossSaleCatalogItem: (state: State, catalogItem: CrossSaleCatalogItem) => {
            state.storedCrossSaleCatalogItem = catalogItem
        },
        clearStoredCrossSaleCatalogItem(state: State) {
            state.storedCrossSaleCatalogItem = null;
        },
        submitted(state: State) {
            state.shoppingCartData.submitted = true;
        },
        submitOrder(state: State, installationOption: InstallationOption) {
            state.shoppingCartData = { id: state.shoppingCartData.id, customer: state.shoppingCartData.customer, token: state.shoppingCartData.token  } as ShoppingCartData;
            state.storedItem = null;
            state.validation = {};
            state.isCustomer = true;
            state.selectedInstallationOption = installationOption;
        },
        setEmailForPromotions(state: State, email: string) {
            state.emailForPromotions = email;
        },
        setIsEmployee(state: State, isEmployee: boolean) {
            state.isEmployee = isEmployee;
        },
        setDateForScheduledCall(state: State, date: string) {
            state.dateForScheduledCall = date;
        },
        setTimeRangeForScheduledCall(state: State, timeRange: string) {
            state.timeRangeForScheduledCall = timeRange;
        },
        setSelectedInstallationOption(state: State, installationOption: InstallationOption) {
            state.selectedInstallationOption = installationOption;
        },
        setFollowUpId(state: State, followUpId: string) {
            state.shoppingCartData.customer = { ...state.shoppingCartData.customer, followUpId } as CustomerInfo;
        },
        setRescheduleData(state: State, data: { customer: CustomerInfo, cartId: string, orderToken: string }) {
            state.shoppingCartData.customer = { ...state.shoppingCartData.customer, ...data.customer };
            state.shoppingCartData.id = data.cartId;
            state.shoppingCartData.token = data.orderToken;
        },
        setProductIdForCrossSaleCatalogItem(state: State, productId: string){
            state.productIdForCrossSaleCatalogItem = productId;
        },
        setBusinessResidence(state: State, businessResidence: BusinessResidence){
            state.shoppingCartData.businessResidence = businessResidence;
        },
        setShouldShowFiberComingSoon(state: State, showFiberComingComingSoon: boolean) {
            state.shouldShowFiberComingSoon = showFiberComingComingSoon;
        },
        setCatItemBeingProcessed(state: State, selectedItem: SelectedServiceProduct) {
            const catIdToIndexWith = selectedItem.productCatId ? selectedItem.productCatId : selectedItem.serviceCatId;
            if (catIdToIndexWith) {
                state.catItemBeingProcessed = { [catIdToIndexWith!]: selectedItem };
            } else {
                state.catItemBeingProcessed = {};
            }
        },
        setNetworkAccessChargeMessageShown(state: State, newValue: boolean) {
            state.notifications.networkAccessChargeMessageShown = newValue;
        },
        setIsTavern(state: State, payload: { isTavern: boolean}) {
            state.shoppingCartData.isTavern = payload.isTavern;
        }
    },
    getters: {
        isInCart: (state: State, {getShoppingCartSelection}) => (item: SelectedServiceProduct): boolean|undefined => {
            const cartSelection = getShoppingCartSelection();
            if (cartSelection && cartSelection.services) {
                if (item.productCatId && item.productId) {
                    return cartSelection.services.some(({serviceId, catId: serviceCatId, products}: Service) => {
                        if (!products) { return false; }
                        if (!isEqual(serviceId, item.serviceId) || (serviceCatId && !isEqual(serviceCatId, item.serviceCatId))) { return false}

                        return products.some(({productId, catId: productCatId}: Product) => isEqual(productId, item.productId)
                            && (productCatId && isEqual(productCatId, item.productCatId)));
                    });
                }

                // checking if a service is selected, no products value was passed in
                return cartSelection.services.some(({serviceId, catId: serviceCatId}: Service) =>
                    isEqual(serviceId, item.serviceId) && serviceCatId && isEqual(serviceCatId, item.serviceCatId));
            }
        },
        isCartEmpty: (state: State) => {
            return !state.shoppingCartData || !state.shoppingCartData.selectedOptions
            || !state.shoppingCartData.selectedOptions.services
            || state.shoppingCartData.selectedOptions.services.length === 0;
        },
        isTavernTvInCart: (state: State) => {
            return state.shoppingCartData.selectedOptions &&
                state.shoppingCartData.selectedOptions.services &&
                state.shoppingCartData.selectedOptions.services.some( (service: Service) => { return service.serviceCatId === 'FAKE-TAVERN'});
        },
        getCartNumber: (state: State) => (): string|number|undefined => {
            return state.shoppingCartData.id;
        },
        getIsCustomer: (state: State): boolean => {
            return state.isCustomer;
        },
        getShoppingCartSelection: (state: State) => (): any|undefined => {
            const shoppingCart = state.shoppingCartData;
            if (shoppingCart) {
                if (shoppingCart.selectedOptions && shoppingCart.selectedOptions.cartTotal) {
                    if (shoppingCart.selectedOptions.services) {
                        shoppingCart.selectedOptions.services.forEach( (service: Service) => service.removable = service.catId !== FAKE_TAVERN_CAT_ID);
                        // convert main ids to strings
                        if (state.shoppingCartData.isTavern && !shoppingCart.selectedOptions.services.some((service: Service) => {
                            return service.serviceCatId === 'FAKE-TAVERN'
                        })) {
                            shoppingCart.selectedOptions.services.push({
                                amount: '-',
                                serviceTypeName: FAKE_TAVERN_NAME,
                                serviceId: FAKE_TAVERN_SERVICE_ID,
                                catId: FAKE_TAVERN_CAT_ID,
                                serviceTypeId: ServiceTypeId.TV,
                                serviceCatId: 'FAKE-TAVERN',
                                name: 'Tavern TV',
                                removable: false
                            })
                        }
                        const services = shoppingCart.selectedOptions.services
                        .map(({products, ...service}) => ({
                            ...service,
                            serviceCatId: (service.catId || ''),
                            products: (products || []).map(({typeId, ...product}) => ({
                                ...product,
                                typeId: typeId ? Number(typeId) : -1,
                                productCatId: (product.catId || '')
                            }))
                        }));

                        return {...shoppingCart.selectedOptions, services};
                    }
                    return shoppingCart.selectedOptions;
                } else {
                    return {};
                }
            }
        },
        getLocation: (state: State): ServiceAddress | undefined => {
            const shoppingCart = state.shoppingCartData;
            if (shoppingCart && shoppingCart.serviceAddress) {
                return {
                    addressId: shoppingCart.addressId,
                    serviceAddress: shoppingCart.serviceAddress,
                    serviceUnit: shoppingCart.serviceUnit,
                    serviceCity: shoppingCart.serviceCity,
                    serviceCityDisplayName: shoppingCart.serviceCityDisplayName,
                    serviceState: shoppingCart.serviceState,
                    serviceZipCode: shoppingCart.serviceZipCode,
                    wireCenter: shoppingCart.wireCenter
                } as ServiceAddress;
            }
            else{
                const currentLocation = sessionStorage.getItem('currentAddress');
                if (currentLocation) {
                    const location = JSON.parse(currentLocation);
                    return {
                        addressId: location.result.addressId,
                        serviceAddress: location.result.serviceAddress,
                        serviceCityDisplayName: location.result.cityDescription,
                        serviceUnit: location.result.Unit,
                        serviceCity: location.result.city,
                        serviceState: location.result.serviceState,
                        serviceZipCode: location.result.zipCode,
                    } as ServiceAddress;
                }
            }
        },
        getServiceTypeById: (state: State, {getAvailableServices}) => (serviceTypeId: string): ServiceType | undefined => {
            const serviceTypes = getAvailableServices() as ServiceType[];
            if (serviceTypes) {
                const serviceType = serviceTypes.find(({id}: ServiceType) => isEqual(id, serviceTypeId)) as ServiceType;
                if (serviceType) {
                    return {...serviceType};
                }
            }
        },
        getServiceByTypeIdAndId: (state: State, {getServiceTypeById}) => (serviceTypeId: string, serviceId: string): Service | undefined => {
            const serviceType = getServiceTypeById(serviceTypeId)  as ServiceType;
            if (serviceType && serviceType.services && serviceType.services.length) {
                const service = serviceType.services.find((svc: Service) => isEqual(svc.serviceId, serviceId));
                if (service) {
                    return {...service};
                }
            }
        },
        getAvailableServices: (state: State) => (): ServiceType[] | undefined => {
            const shoppingCart = state.shoppingCartData;
            if (shoppingCart && shoppingCart.availableOptions && shoppingCart.availableOptions.serviceTypes) {
                // convert main IDs to string
                return shoppingCart.availableOptions.serviceTypes
                .map(({services, ...serviceType}) => ({
                    ...serviceType,
                    serviceTypeId: serviceType.id,
                    services: (services || []).map(({categories, ...service}) => ({
                        ...service,
                        removable: true,
                        serviceCatId: (service.catId || ''),
                        categories: (categories || []).map(({products, ...category}) => ({
                            ...category,
                            products: (products || []).map(({typeId, ...product}) => ({
                                ...product,
                                typeId: typeId ? Number(typeId) : -1,
                                productCatId: (product.catId || '')
                            }))
                        }))
                    }))
                }));
            }
        },
        getStoredItem: (state: State) => (): SelectedServiceProduct|null => {
            return state.storedItem;
        },
        getValidation: (state: State) => (): Validation => {
            return state.validation;
        },
        getErrors: (state: State) => (): CartError[] | undefined => {
            if (state.shoppingCartData && state.shoppingCartData.errors) {
                return state.shoppingCartData.errors;
            }
        },
        getCustomerInfo: (state: State): CustomerInfo | undefined => {
            return state.shoppingCartData.customer;
        },
        cartHasServiceType: (state: State, {getShoppingCartSelection}) => (serviceTypeId: string): boolean|undefined => {
            const cartSelection = getShoppingCartSelection();
            if (cartSelection && cartSelection.services) {
                if (serviceTypeId) {
                    return cartSelection.services.some( (service: Service) => { return service.serviceTypeId === serviceTypeId});
                }
                return false;
            }
        },
    },
    actions: {
        async loadShoppingCart({ commit, state }, params: {cartId: string, token: string, isEmployee: boolean, forceClear: boolean}) {
            commit('setLoadingCart', true);
            commit('setIsEmployee', (params && params.isEmployee) || false);
            if (params && ((params.token && params.cartId) || params.forceClear)){
                // Clear Shopping Cart Data
                commit('clearShoppingCart');
            }
            if (!(state.states && state.states.length)) {
                commit('setStates', await getStates());
            }
            const {serviceAddress} = state.shoppingCartData;
            const token = (params && params.token) || state.shoppingCartData.token || '';
            const id = (params && params.cartId) || (serviceAddress && state.shoppingCartData.id) || '';
            commit('setShoppingCart', await cartData(getCartDataRequest(state.shoppingCartData, token), id));
            commit('setLoadingCart', false);
        },
        async updateOrderDetails({ commit, dispatch, state }, params: {mutation: string, item: SelectedServiceProduct | SelectedLocation | CustomerWithContract}) {
            commit(params.mutation, params.item);
            // don't fetch for new cart data from api
            if (params.item && params.item.noRefetch) {
                return;
            }
            await dispatch('loadShoppingCart', { isEmployee: state.isEmployee });
        },
        async loadCrossSales({ commit, state }, params: { pageId: number, audience: CrossSaleAudienceType}) {
            const crossSalesRequest = {...params, cartId: state.shoppingCartData.id};
            commit('setCrossSales', await loadCrossSales(crossSalesRequest));
        },
        async setTavern({ commit, dispatch, state }, params: { isTavern: boolean}) {
            commit('setIsTavern', params);
            await dispatch('loadShoppingCart', { isEmployee: state.isEmployee });
        }
    },
    plugins: plugins
});

function isEqual(val1: number|string|undefined, val2: number|string|undefined): boolean {
    if (val1 === val2) {
        return true;
    }
    return Boolean(val1 && val2 && val1.toString() === val2.toString());
}

function getCartDataRequest(cartData: ShoppingCartData, token: string|undefined): CartDataRequest {
    let selectedServices: SelectedService[];
    if (cartData.selectedOptions && cartData.selectedOptions.services) {
        selectedServices = cartData.selectedOptions.services.map(service => {
            return {
                serviceId: service.serviceId,
                catId: service.catId || '',
                value: service.value,
                products: !service.products ? [] : service.products.map(product => {
                    return {
                        productId: product.productId,
                        catId: product.catId || '',
                        value: product.value
                    };
                })
            };
        });
    } else {
        selectedServices = [];
    }

    let customerToSend = cartData.customer;
    // clear out customer on request if they have follow up id. At this point, this customer has submitted their order and is rescheduling a follow up call
    // if they hit this code, we should treat them like a brand new customer
    if (customerToSend && customerToSend.followUpId) {
        customerToSend = undefined;
    }

    return {
        addressId: cartData.addressId || '',
        serviceAddress: cartData.serviceAddress || '',
        serviceCity: cartData.serviceCity || '',
        serviceUnit: cartData.serviceUnit || '',
        selectedContractId: cartData.selectedContractId || '',
        customer: customerToSend,
        services: selectedServices,
        queryLogId: cartData.queryLogId || '',
        businessResidence: cartData.businessResidence,
        token,
        isTavern: cartData.isTavern || false
    };
}
